import {
  Card,
  Control,
  Language,
  Notification,
  Player,
} from '@yleisradio/areena-types';
import { useListEngagementData } from 'components/SnowplowEngagementAnalytics/hooks';
import { GroupEngagementData } from 'components/SnowplowEngagementAnalytics/types';
import { useCards } from 'hooks/useCards';
import { useUILanguage } from 'hooks/useUILanguage';
import { getChannelView } from 'services/areena-api/fetchers';
import useSWR from 'swr';
import { findLabel } from 'utils/card';
import { isList } from 'utils/content';

type FetcherParams = ['schedule', channelId: string, language: Language];

function fetcher([, channelId, language]: FetcherParams) {
  return getChannelView(channelId, language);
}

export function useScheduleData(channelId: string): {
  channelLinks: Control[];
  channelPlayer: Player | undefined;
  currentProgram: Card | undefined;
  groupEngagementData: GroupEngagementData;
  upcomingPrograms: Card[];
  errored: boolean;
  notifications: Notification[];
  currentProgramEntityId?: string | undefined;
  scheduleListKey: string | undefined;
} {
  const language = useUILanguage();

  const { data: channelViewResponse, error: channelViewError } = useSWR(
    ['schedule', channelId, language] satisfies FetcherParams,
    fetcher
  );

  const viewNotifications = channelViewResponse?.notifications || [];
  const channelLinks = channelViewResponse?.data?.header?.controls || [];
  const channelPlayer = channelViewResponse?.data?.players?.live;
  const maybeScheduleList = channelViewResponse?.data?.tabs?.[0]?.content?.[0];
  const scheduleList = isList(maybeScheduleList)
    ? maybeScheduleList
    : undefined;
  const scheduleListPointer = scheduleList?.source;

  const {
    cards,
    errored: hasListFetchErrored,
    meta,
    notifications: listNotifications = [],
  } = useCards({
    source: scheduleListPointer,
    pageIndex: 0,
    pageSize: 3,
    refreshInterval: 60_000,
    revalidateOnFocus: true,
  });

  const groupEngagementData = useListEngagementData({
    cardPageMeta: meta,
    isPaginated: false,
    list: scheduleList,
    listIndex: undefined, // currentProgram listIndex should be not set to avoid confusion with first actual list index number
  });

  return {
    channelLinks,
    channelPlayer,
    currentProgram: cards[0],
    groupEngagementData,
    upcomingPrograms: cards.slice(1),
    errored: !!channelViewError || hasListFetchErrored,
    notifications: [...viewNotifications, ...listNotifications],
    currentProgramEntityId: findLabel(
      channelViewResponse?.data?.header?.labels,
      'broadcastService'
    )?.raw,
    scheduleListKey: scheduleListPointer?.uri,
  };
}
